import { DateTime } from "luxon";
import { BookingType } from "../typings/booking-inputs";
import type { BookingInventoryFilterOptions } from "../typings/booking-inventory-filter-options.types";
import type {
  FilterCompatibility,
  FilterCompatibilityItem
} from "./booking-filter-compatibility.functions";

export type FilterCompatibilityBreakdownRow = {
  key: string;
  name: string;
  detail: string;
};

function catalogName(entries: { id: number; name: string }[] | undefined, id: number): string {
  return entries?.find(entry => entry.id === id)?.name ?? String(id);
}

function filterItemName(
  item: FilterCompatibilityItem,
  catalogs: BookingInventoryFilterOptions | undefined,
  bookingType: BookingType | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (item.kind === "equipment") {
    return catalogName(catalogs?.equipment, item.id);
  }
  if (item.kind === "property") {
    return catalogName(catalogs?.bookingProperties, item.id);
  }
  if (item.kind === "equipmentCategory") {
    const name = catalogName(catalogs?.equipmentCategories, item.id);
    if (item.quantity == null) {
      return name;
    }
    return t("_booking_filterCompatibility_itemQuantity", { name, quantity: item.quantity });
  }

  const isZoneMode = bookingType === BookingType.CONFERENCEZONE;
  if (isZoneMode) {
    return catalogName(catalogs?.zoneCategories, item.id);
  }
  return catalogName(catalogs?.placeCategories, item.id);
}

export function formatFilterCompatibilityDayLabel(day: string, locale: string): string {
  const date = DateTime.fromISO(day).setLocale(locale);
  if (!date.isValid) {
    return day;
  }
  return date.toFormat("d LLL");
}

function joinDayLabels(days: string[], locale: string): string {
  return days.map(day => formatFilterCompatibilityDayLabel(day, locale)).join(", ");
}

export function formatFilterCompatibilityItemDetail(
  item: FilterCompatibilityItem,
  bookingDays: string[],
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (bookingDays.length === 0) {
    return t("_booking_filterCompatibility_itemMatchesNoDays");
  }

  const matchedSet = new Set(item.matchedDays);
  const matchedDays = bookingDays.filter(day => matchedSet.has(day));
  const missedDays = bookingDays.filter(day => !matchedSet.has(day));

  if (missedDays.length === 0) {
    return t("_booking_filterCompatibility_itemMatchesAllDays");
  }
  if (matchedDays.length === 0) {
    return t("_booking_filterCompatibility_itemMatchesNoDays");
  }

  return t("_booking_filterCompatibility_itemMatchesSomeDays", {
    matched: joinDayLabels(matchedDays, locale),
    missed: joinDayLabels(missedDays, locale)
  });
}

export function buildFilterCompatibilityBreakdownRows(
  compatibility: FilterCompatibility | undefined,
  catalogs: BookingInventoryFilterOptions | undefined,
  bookingType: BookingType | null | undefined,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string
): FilterCompatibilityBreakdownRow[] {
  const items = compatibility?.items;
  const bookingDays = compatibility?.bookingDays;
  if (!items?.length || !bookingDays?.length) {
    return [];
  }

  return items.map(item => ({
    key: `${item.kind}-${item.id}-${item.quantity ?? "any"}-${item.matchedDays.join("_")}`,
    name: filterItemName(item, catalogs, bookingType, t),
    detail: formatFilterCompatibilityItemDetail(item, bookingDays, locale, t)
  }));
}
